/**
 * Pure in-place assessment status patches for the page-owned units list.
 * GetUnits stamp/epoch handles stale reconciliation — no rank/tombstone merge policy here.
 *
 * These patches are optimistic only. AssessmentCard must still call onRefresh (C251 GetUnits)
 * for E172 / E174 / E180 / E270 — do not remove those card handlers thinking this file alone
 * keeps the unit-card status live (Jul 2026 regression).
 */

export type AssessmentResultFields = {
    e251_AssessmentResultId?: string;
    e251_ResultState?: { key?: string; value?: string };
    e251_Score?: number;
    e251_ResultCompletedAt?: string | null;
};

export type UnitWithAssessmentStatus = {
    e251_UnitId?: string;
    e251_AssessmentId?: string;
    e251_State?: string;
    e251_Touched?: boolean;
    e251_AssessmentStatus?: {
        e251_SharedAssessment?: unknown;
        e251_Result?: AssessmentResultFields;
    };
};

function normalizeId(id: string | null | undefined): string | undefined {
    if (id == null) return undefined;
    const t = String(id).trim().toLowerCase().replace(/[{}]/g, '');
    return t === '' ? undefined : t;
}

/** Compare long ids and raw Guids by the Guid portion when present. */
function idsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
    const na = normalizeId(a);
    const nb = normalizeId(b);
    if (!na || !nb) return false;
    if (na === nb) return true;

    const guidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const ga = na.match(guidRe)?.[0];
    const gb = nb.match(guidRe)?.[0];
    return Boolean(ga && gb && ga === gb);
}

function trimId(id: string | null | undefined): string | undefined {
    if (id == null) return undefined;
    const t = String(id).trim();
    return t === '' ? undefined : t;
}

function unitMatchesAssessmentIds<T extends UnitWithAssessmentStatus>(
    unit: T,
    args: {
        assessmentResultId?: string | null;
        unitId?: string | null;
        assessmentId?: string | null;
    }
): boolean {
    const matchesResult =
        Boolean(args.assessmentResultId) &&
        idsEqual(unit.e251_AssessmentStatus?.e251_Result?.e251_AssessmentResultId, args.assessmentResultId);
    const matchesUnit = Boolean(args.unitId) && idsEqual(unit.e251_UnitId, args.unitId);
    const matchesAssessment =
        Boolean(args.assessmentId) && idsEqual(unit.e251_AssessmentId, args.assessmentId);

    return Boolean(matchesResult || matchesUnit || matchesAssessment);
}

/** Drop e251_Result; keep SharedAssessment when present, else clear status. */
export function stripAssessmentResultFromUnit<T extends UnitWithAssessmentStatus>(unit: T): T {
    const status = unit.e251_AssessmentStatus;
    if (!status?.e251_Result) {
        return unit;
    }

    const shared = status.e251_SharedAssessment;
    if (shared) {
        return {
            ...unit,
            e251_AssessmentStatus: {
                e251_SharedAssessment: shared,
                e251_Result: undefined
            }
        };
    }

    return {
        ...unit,
        e251_AssessmentStatus: undefined
    };
}

export function applyAssessmentResultsDeletedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: {
        assessmentResultId?: string | null;
        unitId?: string | null;
        assessmentId?: string | null;
    }
): T[] {
    if (
        !normalizeId(args.assessmentResultId) &&
        !normalizeId(args.unitId) &&
        !normalizeId(args.assessmentId)
    ) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!unitMatchesAssessmentIds(unit, args)) {
            return unit;
        }
        if (!unit.e251_AssessmentStatus?.e251_Result) {
            return unit;
        }
        anyChange = true;
        return stripAssessmentResultFromUnit(unit);
    });

    return anyChange ? next : units;
}

export function applyAssessmentCompletedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: {
        assessmentResultId?: string | null;
        unitId?: string | null;
        assessmentId?: string | null;
        studentId?: string | null;
        selectedStudentId?: string | null;
    }
): T[] {
    if (
        args.selectedStudentId &&
        args.studentId &&
        !idsEqual(args.selectedStudentId, args.studentId)
    ) {
        return units;
    }

    if (
        !normalizeId(args.assessmentResultId) &&
        !normalizeId(args.unitId) &&
        !normalizeId(args.assessmentId)
    ) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!unitMatchesAssessmentIds(unit, args)) {
            return unit;
        }

        anyChange = true;
        const existingResult = unit.e251_AssessmentStatus?.e251_Result;
        const resultId =
            trimId(args.assessmentResultId) ??
            trimId(existingResult?.e251_AssessmentResultId) ??
            '';

        return {
            ...unit,
            e251_AssessmentStatus: {
                ...(unit.e251_AssessmentStatus || {}),
                e251_Result: {
                    e251_AssessmentResultId: resultId || existingResult?.e251_AssessmentResultId || '',
                    e251_ResultState: {
                        key: 'needs-marking',
                        value: 'Needs Marking'
                    },
                    e251_Score: existingResult?.e251_Score,
                    e251_ResultCompletedAt: existingResult?.e251_ResultCompletedAt ?? null
                }
            }
        };
    });

    return anyChange ? next : units;
}

export function applyAssessmentSharedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: {
        assessmentId?: string | null;
        sharedAssessmentId?: string | null;
        shareType?: string | null;
        linkUrl?: string | null;
    }
): T[] {
    if (!normalizeId(args.assessmentId)) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!idsEqual(unit.e251_AssessmentId, args.assessmentId)) {
            return unit;
        }
        anyChange = true;
        return {
            ...unit,
            e251_AssessmentStatus: {
                ...(unit.e251_AssessmentStatus || {}),
                e251_SharedAssessment: args.sharedAssessmentId
                    ? {
                          e251_SharedAssessmentId: trimId(args.sharedAssessmentId),
                          e251_ShareType: args.shareType || '',
                          e251_LinkUrl: args.linkUrl
                      }
                    : undefined
            }
        };
    });

    return anyChange ? next : units;
}

export function applySharedAssessmentRevokedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    sharedAssessmentId: string | null | undefined
): T[] {
    if (!normalizeId(sharedAssessmentId)) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        const shared = unit.e251_AssessmentStatus?.e251_SharedAssessment as
            | { e251_SharedAssessmentId?: string }
            | undefined;
        if (!idsEqual(shared?.e251_SharedAssessmentId, sharedAssessmentId)) {
            return unit;
        }
        anyChange = true;
        const existingResult = unit.e251_AssessmentStatus?.e251_Result;
        if (existingResult) {
            return {
                ...unit,
                e251_AssessmentStatus: {
                    e251_Result: existingResult,
                    e251_SharedAssessment: undefined
                }
            };
        }
        return {
            ...unit,
            e251_AssessmentStatus: undefined
        };
    });

    return anyChange ? next : units;
}

export function applyAssessmentCancelledToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: { assessmentId?: string | null; unitId?: string | null }
): T[] {
    if (!normalizeId(args.assessmentId) && !normalizeId(args.unitId)) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!unitMatchesAssessmentIds(unit, args)) {
            return unit;
        }
        anyChange = true;
        return {
            ...unit,
            e251_AssessmentStatus: undefined,
            e251_AssessmentId: undefined,
            e251_ResultState: '45'
        };
    });

    return anyChange ? next : units;
}

export function applyAssessmentApprovedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: {
        assessmentId?: string | null;
        unitId?: string | null;
        assessmentResultId?: string | null;
    }
): T[] {
    if (
        !normalizeId(args.assessmentId) &&
        !normalizeId(args.unitId) &&
        !normalizeId(args.assessmentResultId)
    ) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!unitMatchesAssessmentIds(unit, args)) {
            return unit;
        }
        if (!unit.e251_AssessmentStatus?.e251_Result) {
            return unit;
        }
        anyChange = true;
        return {
            ...unit,
            e251_AssessmentStatus: {
                ...unit.e251_AssessmentStatus,
                e251_Result: {
                    ...unit.e251_AssessmentStatus.e251_Result,
                    e251_ResultState: {
                        key: 'teacher-approved',
                        value: 'Teacher Approved'
                    }
                }
            }
        };
    });

    return anyChange ? next : units;
}

export function applyAssessmentStartedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    args: {
        assessmentId?: string | null;
        unitId?: string | null;
        assessmentResultId?: string | null;
    }
): T[] {
    if (
        !normalizeId(args.assessmentId) &&
        !normalizeId(args.unitId) &&
        !normalizeId(args.assessmentResultId)
    ) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!unitMatchesAssessmentIds(unit, args)) {
            return unit;
        }
        anyChange = true;
        const existingResult = unit.e251_AssessmentStatus?.e251_Result;
        const resultId =
            trimId(args.assessmentResultId) ??
            trimId(existingResult?.e251_AssessmentResultId) ??
            '';
        return {
            ...unit,
            e251_AssessmentStatus: {
                ...(unit.e251_AssessmentStatus || {}),
                e251_Result: {
                    e251_AssessmentResultId: resultId || existingResult?.e251_AssessmentResultId || '',
                    e251_ResultState: existingResult?.e251_ResultState?.key
                        ? existingResult.e251_ResultState
                        : { key: 'started', value: 'Started' },
                    e251_Score: existingResult?.e251_Score,
                    e251_ResultCompletedAt: existingResult?.e251_ResultCompletedAt ?? null
                }
            }
        };
    });

    return anyChange ? next : units;
}

export function applyUnitTouchedToUnits<T extends UnitWithAssessmentStatus>(
    units: T[],
    unitId: string | null | undefined
): T[] {
    if (!normalizeId(unitId)) {
        return units;
    }

    let anyChange = false;
    const next = units.map(unit => {
        if (!idsEqual(unit.e251_UnitId, unitId)) {
            return unit;
        }
        if (unit.e251_Touched) {
            return unit;
        }
        anyChange = true;
        return { ...unit, e251_Touched: true };
    });

    return anyChange ? next : units;
}
